import { useMemo } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";

import {
  MAX_CONCATENATED_SEGMENTS,
  UCS2_MAX_CHARS
} from "utils/sms-segments.util";
import {
  SmsTemplateSegmentInfo,
  calculateTemplateSmsSegments
} from "utils/sms-template-length.util";

interface SmsSegmentCounterProps {
  /**
   * Text of the message. When `estimateMergeTags` is false this must be what
   * will actually be sent; when true it may contain literal `{{merge.tags}}`.
   */
  text: string;
  /**
   * Score `{{merge.tags}}` at their estimated resolved length instead of
   * counting the token text. For template surfaces, where a server fills the
   * tags in after the author has stopped looking.
   */
  estimateMergeTags?: boolean;
}

const formatNumber = (value: number) => value.toLocaleString("en-US");

const getCounterLabel = (info: SmsTemplateSegmentInfo): string => {
  // The count is a projection, not a measurement, once tags are in play.
  const prefix = info.estimate?.tagCount ? "about " : "";

  if (info.isOverLimit) {
    return `${prefix}${formatNumber(
      info.charCount
    )} characters — ${formatNumber(-info.charsRemaining)} over the limit`;
  }
  if (info.segmentCount >= MAX_CONCATENATED_SEGMENTS - 1) {
    return `${prefix}${formatNumber(info.charCount)} characters · segment ${
      info.segmentCount
    } of ${MAX_CONCATENATED_SEGMENTS} — ${formatNumber(
      info.charsRemaining
    )} left`;
  }
  return `${prefix}${formatNumber(info.charCount)} characters · ${
    info.segmentCount
  } segment${info.segmentCount === 1 ? "" : "s"}`;
};

export default function SmsSegmentCounter({
  text,
  estimateMergeTags = false
}: SmsSegmentCounterProps) {
  const info = useMemo(
    () => calculateTemplateSmsSegments(text, estimateMergeTags),
    [text, estimateMergeTags]
  );

  const counterColor = info.isOverLimit
    ? "error.main"
    : info.isWithinLastSegment
      ? "warning.dark"
      : grey[700];
  const pipColor = info.isOverLimit
    ? "error.main"
    : info.isWithinLastSegment
      ? "warning.main"
      : "primary.main";

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      flexWrap="wrap"
      data-testid="sms-segment-counter"
      sx={{ pl: 0.25 }}
    >
      <Typography
        sx={{
          color: counterColor,
          fontSize: 12,
          fontVariantNumeric: "tabular-nums"
        }}
      >
        {getCounterLabel(info)}
      </Typography>
      <Stack direction="row" spacing={0.25} aria-hidden sx={{ pt: "1px" }}>
        {Array.from({ length: MAX_CONCATENATED_SEGMENTS }, (_, index) => {
          const isActive =
            index < Math.min(info.segmentCount, MAX_CONCATENATED_SEGMENTS);
          return (
            <Box
              key={index}
              data-testid="sms-segment-pip"
              data-active={isActive}
              sx={{
                width: 5,
                height: 9,
                borderRadius: "1.5px",
                bgcolor: isActive ? pipColor : grey[300]
              }}
            />
          );
        })}
      </Stack>
      {Boolean(info.estimate?.tagCount) && (
        <Typography
          data-testid="sms-merge-tag-notice"
          sx={{ color: grey[700], fontSize: 12 }}
        >
          includes ~{formatNumber(info.estimate!.estimatedTagChars)} for{" "}
          {info.estimate!.tagCount} merge tag
          {info.estimate!.tagCount === 1 ? "" : "s"}
        </Typography>
      )}
      {info.encoding === "ucs2" && info.charCount > 0 && !info.isOverLimit && (
        <Typography
          data-testid="sms-ucs2-notice"
          sx={{ color: "warning.dark", fontSize: 12 }}
        >
          Emoji or special characters reduce the limit to {UCS2_MAX_CHARS}
        </Typography>
      )}
    </Stack>
  );
}
